import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { QuestionnaireTypeCode } from "src/core/constants/folder";
import { useFolder } from "src/core/contexts/FolderContext";
import { Questionnaire, useUpdateAndValidateQuestionnaireMutation } from "src/core/gql/graphql";
import { useToast } from "src/core/hooks/use-toast";
import { IQuestionnaire } from "src/core/interfaces/Questionnaire";
import { mapQuestionnaire } from "src/core/mappers/questionnaire.mapper";
import { calculateQuestionnaireMissingQuestions } from "src/core/utils/folderAnalysis";

export const useQuestionnaireForm = () => {
  const { folderFile, refetch, isReadOnly, isPPH } = useFolder();

  const { toast } = useToast();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const supportingQuestionnaireLAB = useMemo(() => {
    return folderFile.folderFile?.fileCustomerFolder?.supportingQuestionnaires?.find(
      (q) =>
        q.questionnaire?.type.code ===
        (isPPH ? QuestionnaireTypeCode.LAB_PPH : QuestionnaireTypeCode.LAB)
    );
  }, [folderFile, isPPH]);

  const questionnaireId = supportingQuestionnaireLAB?.questionnaire?.id;

  // Est-ce qu'il faut réouvrir le questionnaire ? (isEditable=false)
  const shouldReopen = supportingQuestionnaireLAB
    ? !supportingQuestionnaireLAB.questionnaire?.isEditable
    : false;

  const questionnaireLAB = useMemo<IQuestionnaire | null>(() => {
    if (!supportingQuestionnaireLAB?.questionnaire) return null;
    return mapQuestionnaire(supportingQuestionnaireLAB.questionnaire as Questionnaire);
  }, [supportingQuestionnaireLAB]);

  const message = useMemo<boolean>(() => {
    if (!questionnaireLAB) return false;
    return calculateQuestionnaireMissingQuestions(questionnaireLAB) > 0;
  }, [questionnaireLAB]);

  const { control, reset, handleSubmit } = useForm<Record<string, boolean | undefined>>();

  useEffect(() => {
    if (!questionnaireLAB?.questions) return;

    const defaultValues = questionnaireLAB.questions.reduce<Record<string, boolean | undefined>>(
      (acc, question) => {
        acc[question.code] = question.answer;
        return acc;
      },
      {}
    );

    reset(defaultValues);
  }, [questionnaireLAB, reset]);

  // Afin que le risque soit calculé sur le questionnaire, il est nécessaire de le valider.
  const [updateAndValidateQuestionnaire] = useUpdateAndValidateQuestionnaireMutation();

  const onSubmit = async (data: Record<string, boolean | undefined>) => {
    if (!questionnaireId) {
      return;
    }

    const answers = Object.entries(data).reduce(
      (acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, boolean>
    );

    const content = JSON.stringify(answers);
    try {
      setIsLoading(true);

      // On doit pouvoir enregistrer le questionnaire sans le valider (par exemple si celui-ci n'est pas complet).
      const shouldValidate = Object.entries(data).every(([, value]) => value !== undefined);

      await updateAndValidateQuestionnaire({
        variables: {
          questionnaireId: questionnaireId,
          questionnaireIdOrReference: questionnaireId,
          data: content,
          shouldReopen: shouldReopen,
          shouldValidate: shouldValidate,
        },
      });

      await refetch();

      toast({
        description: t("global.form.success"),
      });
    } catch (error) {
      toast({
        description: t("global.form.fail"),
        variant: "destructive",
      });
      console.error("Erreur lors de la sauvegarde:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    questionnaireLAB,
    control,
    handleSubmit,
    onSubmit,
    isLoading,
    message,
    isReadOnly,
  };
};
